

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SellerInviteResponse, SellerView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import {
  invalidateAdminSellers,
  invalidateAdminStores,
  invalidateAdminSubscriptions,
} from "@/lib/query/core/invalidation";

type CreateSellerInput = {
  name: string;
  email: string;
  phone: string;
  storeName: string;
  subscriptionPlanId: string;
  subscriptionReason: string;
  subscriptionStartDate?: string;
  subscriptionOverrideAmount?: number;
  subscriptionOverridePeriodDays?: number;
};

type CreateSellerResponse = SellerInviteResponse & {
  seller?: SellerView;
};

export function useCreateSellerMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: CreateSellerInput) =>
      apiJsonClient<CreateSellerResponse>(endpoints.admin.sellers, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: async () => {
      await Promise.all([
        invalidateAdminSellers(queryClient),
        invalidateAdminStores(queryClient),
        invalidateAdminSubscriptions(queryClient),
      ]);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
